import classNames from "classnames";
import { useEffect, useState } from "react";
import { CountingFrom } from "../http/onlyclicksApi";
import { translate } from "../i18n";
import { Language } from "../types";
import { IconButton } from "./Button";
import Add from "./icons/add.svg?react";
import Remove from "./icons/remove.svg?react";
import styles from "./Stepper.module.css";

type Size = "regular" | "large";

interface Props {
  value?: number;
  minValue?: number;
  maxValue?: number;
  step?: number;
  unit: string;
  onChange?: (value: number | undefined) => void;
  size?: Size;
  autoFocus?: boolean;
  className?: string;
  countingFrom: CountingFrom;
}

const fixRoundings = (value: number, inc: number): number => {
  return Math.round((value + inc) * 100) / 100;
};

const normalizeInputValue = (input: string) => {
  return input.replace(",", ".");
};

const isPsi = (unit: string): boolean => {
  return unit === "psi" || unit === "Psi" || unit === "PSI";
};

const isBar = (unit: string): boolean => {
  return unit === "bar" || unit === "Bar" || unit === "BAR";
};

const isPsiOrBar = (unit: string): boolean => {
  return isPsi(unit) || isBar(unit);
};

const Stepper = ({
  value,
  minValue,
  maxValue,
  step = 1,
  unit,
  onChange,
  size = "regular",
  autoFocus = false,
  className,
  countingFrom,
}: Props) => {
  const [inputValue, setInputValue] = useState(value?.toString() ?? "");
  const i18n = translate(Language.English);

  useEffect(() => {
    setInputValue(
      value !== undefined
        ? Intl.NumberFormat(navigator.language, {
            signDisplay: "auto",
            maximumFractionDigits: isPsi(unit) ? 1 : isBar(unit) ? 2 : 0,
          }).format(value)
        : "",
    );
  }, [value]);

  const handleDecrement = () => {
    const valueOrDefault = value === undefined || isNaN(value) ? 0 : value;
    const newValue =
      minValue !== undefined && valueOrDefault <= minValue
        ? valueOrDefault
        : fixRoundings(valueOrDefault, -step);
    onChange?.(newValue);
  };

  const handleIncrement = () => {
    const valueOrDefault = value === undefined || isNaN(value) ? 0 : value;
    const newValue =
      maxValue !== undefined && valueOrDefault >= maxValue
        ? value
        : fixRoundings(valueOrDefault, step);
    onChange?.(newValue);
  };

  const handleInputBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === "") return;
    const normalizedValue = normalizeInputValue(e.target.value);
    const parsedValue = isPsiOrBar(unit)
      ? parseFloat(normalizedValue)
      : parseInt(normalizedValue, 10);
    onChange?.(
      parsedValue > 0 && countingFrom === "fully_closed" && unit === i18n.clicks
        ? parsedValue * -1
        : parsedValue,
    );
  };

  return (
    <div className={className}>
      <div
        className={classNames(styles.stepper, {
          [styles.large]: size === "large",
        })}
      >
        <IconButton
          glyph={Remove}
          className={styles.iconBtn}
          rounded={true}
          size={size === "regular" ? "default" : "large"}
          buttonProps={{
            disabled: minValue !== undefined && minValue === value,
            onClick: handleDecrement,
          }}
        />
        <input
          ref={(input) => {
            if (autoFocus) input?.focus();
          }}
          type="text"
          inputMode={isPsiOrBar(unit) ? "decimal" : "numeric"}
          className={styles.input}
          value={inputValue}
          min={minValue}
          max={maxValue}
          placeholder="0"
          step={step}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleInputBlur}
        />
        <IconButton
          glyph={Add}
          className={styles.iconBtn}
          rounded={true}
          size={size === "regular" ? "default" : "large"}
          buttonProps={{
            disabled: maxValue !== undefined && maxValue === value,
            onClick: handleIncrement,
          }}
        />
      </div>
      {unit && <span className={styles.unit}>{unit}</span>}
    </div>
  );
};

export default Stepper;
